import { atom, useAtomValue } from 'jotai';

/**
 * Feature flag for the PlanetSpark classroom UI (the 340px rail of call tiles that
 * replaces grid/speaker view, plus the matching pre-join screen skin).
 *
 * Everything PlanetSpark-specific is gated on this so the stock Daily prebuilt UI is
 * completely unchanged when the flag is off -- no layout swap, no restyled haircheck.
 *
 * Resolution order:
 *   1. ?psClassroom=1 / ?psClassroom=0 in the URL  (per-session override, for testing
 *      and for staged rollout)
 *   2. NEXT_PUBLIC_PS_CLASSROOM_UI=1               (deployment default)
 *   3. off
 *
 * The URL override is listed first deliberately: the room URL is built by
 * DailyIframe.wrap() on the embedding page, so a query param is not always available,
 * but when it is present it must win over the build-time default.
 *
 * Read once at module init (same pattern as preferredViewModeAtom in ./layout) rather
 * than in an effect, so the first paint is already the correct layout instead of
 * flashing the stock UI and swapping.
 */
export const isPsClassroomEnabled = (): boolean => {
  const envDefault = process.env.NEXT_PUBLIC_PS_CLASSROOM_UI === '1';
  if (typeof window === 'undefined') {
    return envDefault;
  }
  try {
    const param = new URLSearchParams(window.location.search).get('psClassroom');
    if (param === '1') return true;
    if (param === '0') return false;
  } catch (e) {
    // Malformed search string: fall through to the deployment default.
  }
  return envDefault;
};

const psClassroomUIAtom = atom<boolean>(isPsClassroomEnabled());

/**
 * Returns whether the PlanetSpark classroom UI is enabled.
 */
export const usePsClassroomUI = (): boolean => useAtomValue(psClassroomUIAtom);

/**
 * Whether the EMBEDDING page renders its own Mic/Camera/Leave controls.
 *
 * Sent by galaxy as ?psParentControls=1. Kept separate from psClassroom on purpose:
 * opening the prebuilt app directly with ?psClassroom=1 must still show a working
 * tray -- suppressing the mic control there would leave no way to unmute at all.
 * Only the duplicated controls are hidden; People / Share / More stay, since the
 * parent bar does not carry those yet.
 */
const getInitialPsParentControls = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('psParentControls') === '1';
  } catch (e) {
    return false;
  }
};

const psParentControlsAtom = atom<boolean>(getInitialPsParentControls());

/**
 * Returns whether the embedding page owns the AV controls.
 */
export const usePsParentControls = (): boolean => useAtomValue(psParentControlsAtom);
